/**
 * CoolerIdDialog — modal popup for editing CoolerID per bottle on the Sample
 * Registration detail page (IsSampling / Pre-scheduled jobs only).
 *
 * XAF parity: BtnCoolerId_Execute → SampleBottleAllocation_ListView_CoolerId
 *   (SampleRegistrationViewController.cs ~line 431).
 *
 * The XAF popup:
 *  · Filters SampleBottleAllocation by [SampleRegistration.JobID.Oid] = sc.Oid
 *  · Shows SampleID, ContainerID (BottleID), CoolerId columns
 *  · CoolerId is inline-editable
 *  · Has OK (Accept) + Cancel buttons — Accept saves, Cancel discards
 *
 * This dialog reproduces that:
 *  · Fetches GET /api/v1/sample-registration/{jobOid}/cooler-ids
 *  · Shows an AppDialog with an AppDataGrid (SampleID, ContainerID, CoolerID)
 *  · CoolerID is inline-editable; edits staged locally until Save
 *  · Save sends PUT per edited row, then reloads on success
 *  · Cancel discards staged edits (XAF Cancel parity)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow } from '../../../components/confirm';
import { LoaderSymbol } from '../../../components/loading';

interface CoolerRow {
  oid:      string;
  sampleId: string;
  bottleId: string;
  coolerId: string;
}

interface Props {
  open:    boolean;
  jobId:   string;
  jobOid:  string;
  onClose: () => void;
}

export function CoolerIdDialog({ open, jobId, jobOid, onClose }: Props) {
  const notify  = useNotify();
  const runSave = useSaveFlow();

  const [rows,       setRows]       = useState<CoolerRow[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [coolerEdits, setCoolerEdits] = useState<Record<string, string>>({});
  const [saving,     setSaving]     = useState(false);

  const load = useCallback(async () => {
    if (!jobOid) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/sample-registration/${jobOid}/cooler-ids`, {
        silentLoading: true,
      } as never);
      setRows((res.data as { data: CoolerRow[] }).data ?? []);
      setCoolerEdits({});
    } catch {
      notify.error('Failed to load CoolerID data.');
    } finally {
      setLoading(false);
    }
  }, [jobOid, notify]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  const stageCoolerEdit = useCallback((row: CoolerRow, newValue: string) => {
    const trimmed = newValue.trim();
    setCoolerEdits(prev => {
      const next = { ...prev };
      if (trimmed === row.coolerId) delete next[row.oid];
      else next[row.oid] = trimmed;
      return next;
    });
  }, []);

  const dirtyCount = Object.keys(coolerEdits).length;

  const cancelEdits = useCallback(() => {
    setCoolerEdits({});
  }, []);

  const save = useCallback(() => {
    if (dirtyCount === 0) { notify.info('No changes to save.'); return; }
    setSaving(true);
    runSave({
      formName: 'Sample Registration',
      auditAction: 'edit',
      save: async () => {
        for (const [oid, value] of Object.entries(coolerEdits)) {
          await api.put(`/api/v1/sample-registration/${jobOid}/cooler-ids/${oid}`,
            { coolerId: value || null });
        }
        await load();
      },
      successMessage: `Updated ${dirtyCount} CoolerID(s).`,
    }).finally(() => setSaving(false));
  }, [dirtyCount, coolerEdits, jobOid, notify, runSave, load]);

  const cols: AppColumn<CoolerRow>[] = useMemo(() => [
    { field: 'sampleId', header: 'Sample ID',    width: 140 },
    { field: 'bottleId', header: 'Container ID', width: 120 },
    { field: 'coolerId', header: 'CoolerID',     width: 140,
      value: r => coolerEdits[r.oid] ?? r.coolerId,
      text:  r => coolerEdits[r.oid] ?? r.coolerId,
      edit: { type: 'text', trigger: 'click' } },
  ], [coolerEdits]);

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <button className="ca-btn ca-btn-solid" onClick={save}
        disabled={saving || dirtyCount === 0}>
        {saving
          ? <><LoaderSymbol size={13} /> Saving…</>
          : <>💾 Save</>}
      </button>
      <button className="ca-btn" onClick={cancelEdits}
        disabled={saving || dirtyCount === 0}>
        ✕ Cancel changes
      </button>
      <button className="ca-btn" onClick={onClose}>Close</button>
    </div>
  );

  if (!open) return null;

  return (
    <AppDialog
      open={open}
      title={`CoolerID — ${jobId}`}
      icon="ti-box"
      width={480}
      onClose={onClose}
      footer={footer}
      maximizable
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <LoaderSymbol size={32} />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ai-muted)' }}>
          No bottle allocation records found for this job.
        </div>
      ) : (
        <div style={{ minHeight: 200 }}>
          <AppDataGrid
            rows={rows}
            columns={cols}
            getRowId={r => r.oid}
            defaultPageSize={50}
            onCellCommit={(row, col, val) => {
              if (col.field === 'coolerId') stageCoolerEdit(row, val);
            }}
          />
          {dirtyCount > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ai-muted)', marginTop: 4 }}>
              {dirtyCount} unsaved change{dirtyCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </AppDialog>
  );
}
